import { MORE_SHAPE_BUILDERS } from './moreShapes';
import type { MoreShapeType } from './moreShapes';
import { polygon } from './shapeGeometry';
import type { Box } from './shapeGeometry';

/** Shapes a node can be drawn as: the app's types, Phase 6 (D-033) and the library sidebar's (D-038). */
export type ShapeType =
  | MoreShapeType
  | 'rectangle'
  | 'square'
  | 'custom'
  | 'round-rectangle'
  | 'circle'
  | 'ellipse'
  | 'oval'
  | 'triangle'
  | 'diamond'
  | 'parallelogram'
  | 'hexagon'
  | 'cylinder'
  | 'arrow-rectangle'
  | 'plus';

const HALF = 0.5;
const THIRD = 1 / 3;
/** Corner radius of `round-rectangle`, as a share of the shorter side. */
const ROUND_CORNER_SHARE = 0.15;
/** Horizontal slant of `parallelogram` and the cut of `hexagon`, as a share of the width. */
const SLANT_SHARE = 0.2;
/** Height of the top / bottom ellipse of `cylinder`, as a share of the height. */
const CYLINDER_CAP_SHARE = 0.1;
/** Length of the point of `arrow-rectangle`, as a share of the width (at most half the height). */
const ARROW_TIP_SHARE = 0.25;

const rectangle = ({ x, y, width, height }: Box): string =>
  polygon([{ x, y }, { x: x + width, y }, { x: x + width, y: y + height }, { x, y: y + height }]);

const roundRectangle = ({ x, y, width, height }: Box): string => {
  const r = Math.min(width, height) * ROUND_CORNER_SHARE;
  const right = x + width;
  const bottom = y + height;
  return `M${x + r} ${y}L${right - r} ${y}Q${right} ${y} ${right} ${y + r}L${right} ${bottom - r}`
    + `Q${right} ${bottom} ${right - r} ${bottom}L${x + r} ${bottom}Q${x} ${bottom} ${x} ${bottom - r}`
    + `L${x} ${y + r}Q${x} ${y} ${x + r} ${y}Z`;
};

const ellipse = ({ x, y, width, height }: Box): string => {
  const rx = width * HALF;
  const ry = height * HALF;
  const cy = y + ry;
  return `M${x} ${cy}A${rx} ${ry} 0 1 0 ${x + width} ${cy}A${rx} ${ry} 0 1 0 ${x} ${cy}Z`;
};

const cylinder = ({ x, y, width, height }: Box): string => {
  const rx = width * HALF;
  const ry = height * CYLINDER_CAP_SHARE;
  const right = x + width;
  const top = y + ry;
  const bottom = y + height - ry;
  const body = `M${x} ${top}L${x} ${bottom}A${rx} ${ry} 0 0 0 ${right} ${bottom}L${right} ${top}A${rx} ${ry} 0 0 0 ${x} ${top}Z`;
  const rim = `M${x} ${top}A${rx} ${ry} 0 0 0 ${right} ${top}`;
  return body + rim;
};

const plus = ({ x, y, width, height }: Box): string => {
  const [x1, x2] = [x + width * THIRD, x + width * 2 * THIRD];
  const [y1, y2] = [y + height * THIRD, y + height * 2 * THIRD];
  const [right, bottom] = [x + width, y + height];
  return polygon([
    { x: x1, y }, { x: x2, y }, { x: x2, y: y1 }, { x: right, y: y1 }, { x: right, y: y2 }, { x: x2, y: y2 },
    { x: x2, y: bottom }, { x: x1, y: bottom }, { x: x1, y: y2 }, { x, y: y2 }, { x, y: y1 }, { x: x1, y: y1 },
  ]);
};

const BUILDERS: Record<ShapeType, (box: Box) => string> = {
  ...MORE_SHAPE_BUILDERS,
  rectangle,
  square: rectangle,
  custom: rectangle,
  'round-rectangle': roundRectangle,
  circle: ellipse,
  ellipse,
  oval: ellipse,
  cylinder,
  plus,
  triangle: ({ x, y, width, height }) => polygon([{ x: x + width * HALF, y }, { x: x + width, y: y + height }, { x, y: y + height }]),
  diamond: ({ x, y, width, height }) => polygon([
    { x: x + width * HALF, y }, { x: x + width, y: y + height * HALF }, { x: x + width * HALF, y: y + height }, { x, y: y + height * HALF },
  ]),
  parallelogram: ({ x, y, width, height }) => {
    const slant = width * SLANT_SHARE;
    return polygon([{ x: x + slant, y }, { x: x + width, y }, { x: x + width - slant, y: y + height }, { x, y: y + height }]);
  },
  hexagon: ({ x, y, width, height }) => {
    const cut = width * SLANT_SHARE;
    return polygon([
      { x: x + cut, y }, { x: x + width - cut, y }, { x: x + width, y: y + height * HALF },
      { x: x + width - cut, y: y + height }, { x: x + cut, y: y + height }, { x, y: y + height * HALF },
    ]);
  },
  'arrow-rectangle': ({ x, y, width, height }) => {
    const tip = Math.min(width * ARROW_TIP_SHARE, height * HALF);
    return polygon([
      { x, y }, { x: x + width - tip, y }, { x: x + width, y: y + height * HALF }, { x: x + width - tip, y: y + height }, { x, y: y + height },
    ]);
  },
};

export const isShapeType = (type: string | undefined): type is ShapeType => type !== undefined && type in BUILDERS;

/**
 * SVG outline of `type` filling a `width` × `height` box, pulled in by `inset` on every side so a stroke of
 * width `2 × inset` stays inside the box. Unknown types draw as a rectangle.
 */
export const getShapePath = (type: string, width: number, height: number, inset = 0): string => {
  const box = { x: inset, y: inset, width: Math.max(width - 2 * inset, 0), height: Math.max(height - 2 * inset, 0) };
  return (isShapeType(type) ? BUILDERS[type] : rectangle)(box);
};
